import { useEffect, useState } from "react";
import { AlertTriangle, Ban, BookOpen, CheckCircle2, Eye, EyeOff, KeyRound, Loader2, Mail, Plug, Plus, RefreshCw, ShieldAlert, ShieldOff, ToggleLeft, Trash2, User, Wrench, PlayCircle, X } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, auth, authHeaders } from "@/lib/api";
import { fetchers, qk, prefetchSettingsTab } from "@/lib/queryClient";
import { SettingsTabSkeleton } from "@/components/PageSkeletons";
import { toast } from "sonner";

const TABS = [
  { key: "profile", label: "Profile", icon: User },
  { key: "flags", label: "Feature Flags", icon: ToggleLeft },
  { key: "keys", label: "Secrets", icon: KeyRound },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "governance", label: "Governance", icon: BookOpen },
  { key: "sources", label: "Sources blocklist", icon: ShieldOff },
  { key: "advanced", label: "Advanced", icon: Wrench },
  { key: "danger", label: "Danger Zone", icon: ShieldAlert },
];

function TabButton({ active, onClick, onHover, icon: Icon, label, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={onHover}
      onFocus={onHover}
      className={
        "flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors " +
        (active
          ? "bg-hm-bg-1 text-hm-fg"
          : "text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-1")
      }
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </button>
  );
}

function ProfileTab({ user }) {
  return (
    <div className="hm-surface p-6 max-w-2xl">
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-hm-bg-2 border border-hm-border-strong flex items-center justify-center text-xl text-hm-fg-muted">
          {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "?"}
        </div>
        <div>
          <div className="text-hm-fg font-medium">{user?.name || "—"}</div>
          <div className="mono text-[11px] text-hm-fg-muted">{user?.email}</div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-1">
            User ID
          </div>
          <div className="mono text-xs text-hm-fg-muted break-all">{user?.id}</div>
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-1">
            Provider
          </div>
          <div className="text-xs text-hm-fg-muted">Google OAuth</div>
        </div>
      </div>
    </div>
  );
}

function FlagsTab() {
  const [busy, setBusy] = useState(null);
  const queryClient = useQueryClient();
  const { data: flags, isPending, error } = useQuery({
    queryKey: qk.featureFlags(),
    queryFn: fetchers.featureFlags,
  });

  const toggle = async (key, enabled) => {
    setBusy(key);
    try {
      const res = await fetch(`${API}/feature-flags/${encodeURIComponent(key)}`, {
        method: "PATCH",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      queryClient.setQueryData(qk.featureFlags(), (prev) =>
        Array.isArray(prev) ? prev.map((r) => (r.key === key ? { ...r, ...updated } : r)) : prev
      );
      toast.success(`${key} → ${enabled ? "on" : "off"}`);
    } catch (e) {
      toast.error(`Failed to toggle: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  if (error) {
    return (
      <div className="hm-surface p-4 text-sm text-hm-danger" data-testid="flags-error">
        {error.message}
      </div>
    );
  }
  if (isPending || !flags) {
    return <SettingsTabSkeleton />;
  }

  return (
    <div className="hm-surface divide-y divide-hm-border" data-testid="flags-panel">
      {flags.map((f) => (
        <div
          key={f.key}
          className="flex items-center justify-between gap-4 px-5 py-4"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <code
                className="mono text-xs text-hm-fg"
                data-testid={`flag-key-${f.key}`}
              >
                {f.key}
              </code>
              {f.enabled && (
                <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-hm-accent-glow text-hm-accent">
                  on
                </span>
              )}
            </div>
            {f.description && (
              <div className="text-xs text-hm-fg-muted mt-1">{f.description}</div>
            )}
          </div>
          <button
            data-testid={`flag-toggle-${f.key}`}
            disabled={busy === f.key}
            onClick={() => toggle(f.key, !f.enabled)}
            className={
              "relative h-6 w-11 rounded-full transition-colors border " +
              (f.enabled
                ? "bg-hm-accent border-hm-accent-dim"
                : "bg-hm-bg-2 border-hm-border-strong")
            }
          >
            <span
              className={
                "absolute top-0.5 h-5 w-5 rounded-full bg-hm-bg transition-transform " +
                (f.enabled ? "translate-x-5" : "translate-x-0.5")
              }
            />
          </button>
        </div>
      ))}
    </div>
  );
}

function KeysTab() {
  const [showAdd, setShowAdd] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState({}); // {key: plaintext}
  const [revealingKey, setRevealingKey] = useState(null);
  const queryClient = useQueryClient();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.settingsSecrets(),
    queryFn: fetchers.settingsSecrets,
  });
  const err = error?.message || null;
  const load = () => {
    queryClient.invalidateQueries({ queryKey: qk.settingsSecrets() });
    return refetch();
  };

  const save = async () => {
    const k = newKey.trim().toUpperCase();
    const v = newValue.trim();
    if (!k || !v) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/settings/secrets`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ key: k, value: v }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      toast.success(`${k} saved`);
      setNewKey("");
      setNewValue("");
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const reveal = async (key) => {
    if (revealed[key]) {
      setRevealed((r) => {
        const cp = { ...r };
        delete cp[key];
        return cp;
      });
      return;
    }
    setRevealingKey(key);
    try {
      const res = await fetch(`${API}/settings/secrets/reveal/${encodeURIComponent(key)}`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setRevealed((r) => ({ ...r, [key]: body.value }));
    } catch (e) {
      toast.error(`Reveal failed: ${e.message}`);
    } finally {
      setRevealingKey(null);
    }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete secret ${key}?`)) return;
    try {
      const res = await fetch(`${API}/settings/secrets/${encodeURIComponent(key)}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${key} deleted`);
      setRevealed((r) => {
        const cp = { ...r };
        delete cp[key];
        return cp;
      });
      load();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  if (err) {
    return (
      <div className="hm-surface p-4 text-sm text-hm-danger" data-testid="secrets-error">
        {err}
      </div>
    );
  }
  if (isPending || !data) {
    return <SettingsTabSkeleton />;
  }

  return (
    <div data-testid="secrets-panel">
      <div className="hm-surface p-4 mb-3 flex items-center gap-3">
        <KeyRound className="h-4 w-4 text-hm-accent" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-hm-fg">Encrypted with Fernet</div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
            key fingerprint …{data.meta?.fernet_fingerprint}
          </div>
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          data-testid="secret-add-toggle"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-hm-accent text-hm-bg text-xs font-medium hover:bg-hm-accent-dim"
        >
          <Plus className="h-3.5 w-3.5" />
          {showAdd ? "Cancel" : "Add secret"}
        </button>
      </div>

      {showAdd && (
        <div className="hm-surface p-4 mb-3 space-y-3">
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle block mb-1">
              Key (SCREAMING_SNAKE_CASE)
            </label>
            <input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value.toUpperCase())}
              placeholder="OPENAI_API_KEY"
              data-testid="secret-new-key"
              className="w-full h-9 px-3 rounded-md bg-hm-bg-1 border border-hm-border mono text-sm text-hm-fg focus:outline-none focus:border-hm-accent-dim"
            />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle block mb-1">
              Value
            </label>
            <input
              type="password"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="sk-…"
              data-testid="secret-new-value"
              className="w-full h-9 px-3 rounded-md bg-hm-bg-1 border border-hm-border mono text-sm text-hm-fg focus:outline-none focus:border-hm-accent-dim"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={save}
              disabled={busy || !newKey.trim() || !newValue.trim()}
              data-testid="secret-save"
              className="px-4 h-8 rounded-md bg-hm-accent text-hm-bg text-sm font-medium disabled:opacity-40"
            >
              {busy ? "Saving…" : "Encrypt & save"}
            </button>
          </div>
        </div>
      )}

      {data.data.length === 0 ? (
        <div className="hm-surface p-8 text-center mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
          No secrets yet.
        </div>
      ) : (
        <div className="hm-surface divide-y divide-hm-border">
          {data.data.map((s) => (
            <div
              key={s.key}
              data-testid={`secret-row-${s.key}`}
              className="px-5 py-4 flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <code className="mono text-xs text-hm-fg">{s.key}</code>
                <div className="mono text-[10px] text-hm-fg-subtle mt-1">
                  updated {new Date(s.updated_at).toLocaleString()}
                </div>
                {revealed[s.key] && (
                  <div
                    className="mt-2 p-2 rounded-md bg-hm-bg-1 border border-hm-accent-dim mono text-xs text-hm-accent break-all"
                    data-testid={`secret-revealed-${s.key}`}
                  >
                    {revealed[s.key]}
                  </div>
                )}
              </div>
              <button
                onClick={() => reveal(s.key)}
                disabled={revealingKey === s.key}
                data-testid={`secret-reveal-${s.key}`}
                className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md text-xs text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-1"
              >
                {revealingKey === s.key ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : revealed[s.key] ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
                {revealed[s.key] ? "Hide" : "Reveal"}
              </button>
              <button
                onClick={() => remove(s.key)}
                data-testid={`secret-delete-${s.key}`}
                className="p-1.5 rounded-md text-hm-fg-subtle hover:text-hm-danger hover:bg-hm-bg-1"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GmailIntegrationCard() {
  const [state, setState] = useState({ loading: true, connected: false, email: null, connected_at: null });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/me/integrations`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const gmail = (body.user_integrations || []).find((i) => i.provider === "gmail");
      setState({
        loading: false,
        connected: !!(gmail && gmail.status === "active"),
        email: gmail?.connected_email || null,
        connected_at: gmail?.connected_at || null,
      });
    } catch (e) {
      setState({ loading: false, connected: false, email: null, connected_at: null });
    }
  };

  useEffect(() => { load(); }, []);

  // Refresh on ?connected=gmail (return from OAuth consent).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connected") === "gmail") {
      toast.success("Gmail connected — you can now send briefs from your inbox.");
      load();
      // Strip the query param so re-mounts don't re-toast.
      const url = new URL(window.location.href);
      url.searchParams.delete("connected");
      window.history.replaceState(null, "", url.toString());
    }
  }, []);

  const connect = () => {
    // Full-page redirect so cookies + state cycle cleanly.
    window.location.assign(`${API}/oauth/google/login?kind=connect_gmail`);
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Gmail? You will need to re-authorize to send from this account.")) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/oauth/google/disconnect`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Gmail disconnected");
      load();
    } catch (e) {
      toast.error(`Disconnect failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hm-surface p-5" data-testid="gmail-integration-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-hm-bg-1 border border-hm-border-strong flex items-center justify-center">
          <Mail className="h-5 w-5 text-hm-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-hm-fg font-medium flex items-center gap-2">
            Gmail delivery
            <span
              className={
                "mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border " +
                (state.connected
                  ? "border-hm-accent-dim text-hm-accent"
                  : "border-hm-border text-hm-fg-subtle")
              }
              data-testid="gmail-status-chip"
            >
              {state.loading ? "loading" : state.connected ? "connected" : "not connected"}
            </span>
          </div>
          <p className="text-xs text-hm-fg-muted mt-1 max-w-lg leading-relaxed">
            Grants Hail Mary the <code className="mono text-[11px]">gmail.send</code> scope so
            you can dispatch daily briefs straight from your Gmail. Nothing is read
            from your inbox — send-only.
          </p>
          {state.connected && (
            <div className="mt-3 flex items-center gap-2 text-xs text-hm-fg" data-testid="gmail-connected-email">
              <CheckCircle2 className="h-3.5 w-3.5 text-hm-accent" />
              Connected as <span className="mono">{state.email}</span>
              {state.connected_at && (
                <span className="mono text-[10px] text-hm-fg-subtle">
                  · since {new Date(state.connected_at).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="shrink-0">
          {state.connected ? (
            <button
              onClick={disconnect}
              disabled={busy}
              data-testid="gmail-disconnect-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Disconnect
            </button>
          ) : (
            <button
              onClick={connect}
              data-testid="gmail-connect-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim"
            >
              <Mail className="h-3.5 w-3.5" />
              Connect Gmail
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DeliverySettingsCard() {
  const [state, setState] = useState({
    loading: true,
    auto_delivery_enabled: false,
    recipients: [],
    delivery_time: "07:55",
    timezone: "Asia/Singapore",
    updated_at: null,
  });
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [recipientsInput, setRecipientsInput] = useState("");
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${API}/daily-brief-v2/delivery-settings`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setState({ loading: false, ...body });
      setRecipientsInput((body.recipients || []).join(", "));
      setDirty(false);
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const recipients = recipientsInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (state.auto_delivery_enabled && recipients.length === 0) {
      toast.error("Add at least one recipient before enabling auto-delivery.");
      return;
    }
    const bad = recipients.find((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    if (bad) {
      toast.error(`Invalid email: ${bad}`);
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/daily-brief-v2/delivery-settings`, {
        method: "PUT",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          auto_delivery_enabled: state.auto_delivery_enabled,
          recipients,
          delivery_time: state.delivery_time,
          timezone: state.timezone,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${r.status}`);
      }
      const body = await r.json();
      setState({ loading: false, ...body });
      setRecipientsInput((body.recipients || []).join(", "));
      setDirty(false);
      toast.success("Delivery settings saved.");
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const triggerNow = async (force = false) => {
    const confirmMsg = force
      ? "Force a resend even if this brief was already delivered? A new email will be sent to every recipient."
      : "Fire the auto-delivery job right now? Already-delivered briefs will be skipped.";
    if (!window.confirm(confirmMsg)) return;
    setTriggering(true);
    try {
      const url = `${API}/daily-brief-v2/trigger-auto-delivery-now${force ? "?force=true" : ""}`;
      const r = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);

      // Explicit branching so the operator never wonders "did it actually
      // send?". Idempotent skips get their own, actionable toast.
      if (body.sent > 0) {
        toast.success(
          `Sent to ${body.sent} recipient${body.sent === 1 ? "" : "s"} · ${body.brief_date || "today"}`,
        );
      } else if (body.skipped > 0 && body.failed === 0) {
        toast(
          `Skipped ${body.skipped} · already delivered today. Click "Force resend" to send anyway.`,
          {
            duration: 6000,
            action: {
              label: "Force resend",
              onClick: () => triggerNow(true),
            },
          },
        );
      } else if (body.failed > 0) {
        toast.error(
          `Failed ${body.failed}${body.sent > 0 ? ` (sent ${body.sent})` : ""} · check backend logs`,
        );
      } else if (!body.brief_id) {
        toast.error(
          `No v2 brief found for today (${body.sgt_today}). Run the pipeline first.`,
        );
      } else {
        toast(
          `No targets · make sure Gmail is connected and auto-delivery is enabled.`,
          { duration: 5000 },
        );
      }
    } catch (e) {
      toast.error(`Trigger failed: ${e.message}`);
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className="hm-surface p-5" data-testid="delivery-settings-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-hm-bg-1 border border-hm-border-strong flex items-center justify-center">
          <PlayCircle className="h-5 w-5 text-hm-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-hm-fg font-medium flex items-center gap-2">
            Auto delivery
            <span
              className={
                "mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border " +
                (state.auto_delivery_enabled
                  ? "border-hm-accent-dim text-hm-accent"
                  : "border-hm-border text-hm-fg-subtle")
              }
              data-testid="delivery-status-chip"
            >
              {state.auto_delivery_enabled ? "on" : "off"}
            </span>
          </div>
          <p className="text-xs text-hm-fg-muted mt-1 max-w-xl leading-relaxed">
            Cron dispatches yesterday's Daily Brief every morning at
            <code className="mono text-[11px] mx-1">{state.delivery_time} {state.timezone}</code>
            to the recipient list below. Requires an active Gmail connection above.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4 items-start">
        <label className="flex items-center gap-2 text-sm text-hm-fg cursor-pointer" data-testid="delivery-toggle">
          <input
            type="checkbox"
            checked={state.auto_delivery_enabled}
            onChange={(e) => {
              setState((s) => ({ ...s, auto_delivery_enabled: e.target.checked }));
              setDirty(true);
            }}
            className="accent-hm-accent"
            data-testid="delivery-toggle-input"
          />
          Enable 07:55 SGT cron
        </label>
        <div className="flex flex-col gap-1">
          <label className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
            Recipients (comma-separated)
          </label>
          <input
            value={recipientsInput}
            onChange={(e) => {
              setRecipientsInput(e.target.value);
              setDirty(true);
            }}
            placeholder="lenka.galinie@gmail.com, ..."
            data-testid="delivery-recipients-input"
            className="w-full h-9 px-3 rounded-md bg-hm-bg-1 border border-hm-border text-sm text-hm-fg focus:outline-none focus:border-hm-accent-dim"
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={save}
          disabled={saving || !dirty}
          data-testid="delivery-save-btn"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-xs bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Save
        </button>
        <button
          onClick={() => triggerNow(false)}
          disabled={triggering || !state.auto_delivery_enabled}
          data-testid="delivery-trigger-now-btn"
          title={
            state.auto_delivery_enabled
              ? "Fire the cron immediately without waiting for 07:55 SGT."
              : "Enable auto-delivery + save first."
          }
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {triggering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
          Trigger now
        </button>
        <button
          onClick={() => triggerNow(true)}
          disabled={triggering || !state.auto_delivery_enabled}
          data-testid="delivery-force-resend-btn"
          title="Bypass the idempotence guard and always send, even if this brief was already delivered."
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs border border-hm-warn/40 text-hm-warn hover:bg-hm-warn/10 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Force resend
        </button>
        {state.updated_at && (
          <span className="ml-auto mono text-[10px] text-hm-fg-subtle">
            saved · {new Date(state.updated_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}

function IntegrationsTab() {
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();

  // 3-way parallel — same cache keys as the individual tabs so switching
  // between Integrations ↔ Flags ↔ Keys is instant after the first hover.
  const flagsQ   = useQuery({ queryKey: qk.featureFlags(),    queryFn: fetchers.featureFlags });
  const secretsQ = useQuery({ queryKey: qk.settingsSecrets(), queryFn: fetchers.settingsSecrets });
  const dlqQ     = useQuery({ queryKey: qk.pushDeadLetter(),  queryFn: fetchers.pushDeadLetter });
  const flags   = flagsQ.data;
  const secrets = secretsQ.data;
  const dlq     = dlqQ.data;
  const isPending = flagsQ.isPending || secretsQ.isPending || dlqQ.isPending;

  const load = () => {
    queryClient.invalidateQueries({ queryKey: qk.featureFlags() });
    queryClient.invalidateQueries({ queryKey: qk.settingsSecrets() });
    queryClient.invalidateQueries({ queryKey: qk.pushDeadLetter() });
  };

  const setBAFlag = async (enabled) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/feature-flags/beyond_activ.push`, {
        method: "PATCH",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`beyond_activ.push → ${enabled ? "on" : "off"}`);
      load();
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const retryDlq = async (did) => {
    try {
      const res = await fetch(`${API}/push/dead-letter/${did}/retry`, {
        method: "POST",
        headers: authHeaders(),
      });
      const body = await res.json();
      toast.info(`Retry: ${body.status}`);
      load();
    } catch (e) {
      toast.error(`Retry failed: ${e.message}`);
    }
  };

  const flag = flags?.find((f) => f.key === "beyond_activ.push");
  const enabled = !!flag?.enabled;
  const secretKeys = (secrets?.data || []).map((s) => s.key);
  const urlSet = secretKeys.includes("BEYOND_ACTIV_API_URL");
  const tokenSet = secretKeys.includes("BEYOND_ACTIV_API_KEY");
  const configured = urlSet && tokenSet;

  if (isPending && !flags && !secrets && !dlq) {
    return <SettingsTabSkeleton />;
  }

  return (
    <div className="space-y-4" data-testid="integrations-panel">
      <GmailIntegrationCard />
      <DeliverySettingsCard />
      <div className="hm-surface p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-hm-bg-1 border border-hm-border-strong flex items-center justify-center">
            <Plug className="h-5 w-5 text-hm-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-hm-fg font-medium flex items-center gap-2">
              Beyond Activ push
              <span
                className={
                  "mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border " +
                  (configured && enabled
                    ? "border-hm-accent-dim text-hm-accent"
                    : configured
                      ? "border-hm-warn/50 text-hm-warn"
                      : "border-hm-border text-hm-fg-subtle")
                }
                data-testid="ba-status-chip"
              >
                {!configured
                  ? "not configured"
                  : !enabled
                    ? "disabled"
                    : "ready"}
              </span>
            </div>
            <p className="text-xs text-hm-fg-muted mt-1 max-w-lg leading-relaxed">
              Approved daily briefs can be pushed to Beyond Activ via a signed
              POST. Configure the URL + API key in the Secrets tab, then flip
              the feature flag below.
            </p>
          </div>
          <button
            onClick={() => setBAFlag(!enabled)}
            disabled={busy}
            data-testid="ba-toggle"
            className={
              "shrink-0 relative h-6 w-11 rounded-full transition-colors border " +
              (enabled
                ? "bg-hm-accent border-hm-accent-dim"
                : "bg-hm-bg-2 border-hm-border-strong")
            }
          >
            <span
              className={
                "absolute top-0.5 h-5 w-5 rounded-full bg-hm-bg transition-transform " +
                (enabled ? "translate-x-5" : "translate-x-0.5")
              }
            />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="hm-surface p-3 border-hm-border">
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              BEYOND_ACTIV_API_URL
            </div>
            <div className={"text-sm mt-1 " + (urlSet ? "text-hm-accent" : "text-hm-fg-subtle")}>
              {urlSet ? "configured" : "empty"}
            </div>
          </div>
          <div className="hm-surface p-3 border-hm-border">
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              BEYOND_ACTIV_API_KEY
            </div>
            <div className={"text-sm mt-1 " + (tokenSet ? "text-hm-accent" : "text-hm-fg-subtle")}>
              {tokenSet ? "configured" : "empty"}
            </div>
          </div>
        </div>
      </div>

      <div className="hm-surface" data-testid="dlq-panel">
        <div className="px-5 py-4 flex items-center gap-3 border-b border-hm-border">
          <AlertTriangle className="h-4 w-4 text-hm-warn" />
          <div className="flex-1">
            <div className="text-sm text-hm-fg">Dead-letter queue</div>
            <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mt-0.5">
              pushes that exhausted retries — retry or investigate
            </div>
          </div>
          <button
            onClick={load}
            className="h-8 w-8 rounded-md flex items-center justify-center text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
            aria-label="Refresh DLQ"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        {!dlq ? (
          <div className="p-4 mono text-[11px] text-hm-fg-subtle">loading…</div>
        ) : dlq.data.length === 0 ? (
          <div className="p-6 text-center mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
            queue is empty — nothing to see here
          </div>
        ) : (
          <div className="divide-y divide-hm-border">
            {dlq.data.map((r) => (
              <div
                key={r.id}
                data-testid={`dlq-row-${r.id}`}
                className="px-5 py-3 flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="mono text-xs text-hm-fg">
                    brief {r.brief_id?.slice(0, 8)}
                  </div>
                  <div className="mono text-[11px] text-hm-fg-muted mt-1">
                    {r.attempts} attempt(s) · {new Date(r.created_at).toLocaleString()}
                  </div>
                  {r.last_error && (
                    <div className="mono text-[10px] text-hm-danger mt-1 break-all">
                      {r.last_error.slice(0, 200)}
                    </div>
                  )}
                </div>
                {r.resolved_at ? (
                  <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-hm-accent-dim text-hm-accent">
                    resolved
                  </span>
                ) : (
                  <button
                    onClick={() => retryDlq(r.id)}
                    data-testid={`dlq-retry-${r.id}`}
                    className="h-8 px-3 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong"
                  >
                    Retry
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DangerTab({ onLogout }) {
  return (
    <div className="hm-surface p-6 border-hm-danger/40">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-hm-danger shrink-0 mt-0.5" />
        <div>
          <div className="text-hm-fg font-medium">Sign out</div>
          <p className="text-sm text-hm-fg-muted mt-1 max-w-md">
            Invalidates your current session immediately. You can sign back in with the same Google account.
          </p>
          <button
            onClick={onLogout}
            data-testid="danger-logout"
            className="mt-4 px-4 py-2 rounded-md bg-hm-danger/10 border border-hm-danger/40 text-hm-danger hover:bg-hm-danger/20 transition-colors text-sm"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Turn CC · Sub-Phase 5 + Turn CC · 9 SP1 — Governance tab (multi-product).
// One textarea per product; switcher pills up top; version + editor
// metadata tracked separately per product.
// ---------------------------------------------------------------------------
const GOV_PRODUCTS = [
  { slug: "product_1_daily_brief",    label: "Product 1 · Daily Brief" },
  { slug: "product_2_weekly_brief",   label: "Product 2 · Weekly Brief" },
  { slug: "product_3_monthly_brief",  label: "Product 3 · Monthly Brief" },
  { slug: "product_4_weekly_quotes",  label: "Product 4 · Weekly Quotes" },
  { slug: "product_5_weekly_survey",  label: "Product 5 · Weekly Survey" },
  { slug: "product_6_weekly_numbers", label: "Product 6 · Weekly Numbers" },
];
const GOV_LS_KEY = "hm_governance_active_product";

function GovernanceTab() {
  // Read initial product from ?product= or localStorage or first item.
  const initialProduct = (() => {
    if (typeof window === "undefined") return GOV_PRODUCTS[0].slug;
    const qp = new URLSearchParams(window.location.search).get("product");
    if (qp && GOV_PRODUCTS.some((p) => p.slug === qp)) return qp;
    const ls = window.localStorage.getItem(GOV_LS_KEY);
    if (ls && GOV_PRODUCTS.some((p) => p.slug === ls)) return ls;
    return GOV_PRODUCTS[0].slug;
  })();

  const [product, setProduct] = useState(initialProduct);
  const [state, setState] = useState({ loading: true });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState(null); // slug awaiting confirm

  // Persist last-viewed product + reflect it in the URL (no page reload).
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(GOV_LS_KEY, product);
    const url = new URL(window.location.href);
    if (url.searchParams.get("tab") === "governance") {
      url.searchParams.set("product", product);
      window.history.replaceState({}, "", url.toString());
    }
  }, [product]);

  const load = async (slug) => {
    setState({ loading: true });
    setEditing(false);
    try {
      const r = await fetch(
        `${API}/governance/charter?product=${encodeURIComponent(slug)}`,
        { headers: authHeaders() },
      );
      if (r.status === 404) {
        setState({ loading: false, not_found: true });
        setDraft("");
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setState({ loading: false, ...body });
      setDraft(body.raw_markdown || "");
    } catch (e) {
      setState({ loading: false, error: e.message });
    }
  };

  useEffect(() => { load(product); }, [product]);

  // Guarded product switching — confirm if unsaved changes exist.
  const requestSwitch = (slug) => {
    if (slug === product) return;
    if (editing && draft.trim() !== (state.raw_markdown || "").trim()) {
      setPendingSwitch(slug);
      return;
    }
    setProduct(slug);
  };
  const confirmSwitch = () => {
    setProduct(pendingSwitch);
    setPendingSwitch(null);
  };
  const cancelSwitch = () => setPendingSwitch(null);

  const startEdit = () => {
    setDraft(state.raw_markdown || "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(state.raw_markdown || "");
    setEditing(false);
  };

  const save = async () => {
    if (draft.trim() === (state.raw_markdown || "").trim()) {
      toast("No changes to save.");
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/governance/charter`, {
        method: "PUT",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ product, raw_markdown: draft }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      setState({ loading: false, ...body });
      setDraft(body.raw_markdown || "");
      setEditing(false);
      toast.success(
        body.no_change
          ? "No changes detected."
          : `Charter updated to ${body.version}. Next LLM run will use this version.`,
      );
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const activeProduct = GOV_PRODUCTS.find((p) => p.slug === product) || GOV_PRODUCTS[0];

  return (
    <div className="mx-auto w-full max-w-3xl" data-testid="governance-panel">
      <div className="hm-surface p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <h2 className="text-hm-fg text-lg font-semibold tracking-tight">
              Editorial Charter
            </h2>
            <p className="text-xs text-hm-fg-muted mt-1 max-w-lg leading-relaxed">
              The source of truth for {activeProduct.label} LLM scoring.
              Injected verbatim into the system prompt on every run.
            </p>
          </div>
          {state && !state.loading && !state.error && !state.not_found && (
            <div className="shrink-0">
              <span
                data-testid="governance-version-chip"
                className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-hm-accent-dim text-hm-accent"
              >
                {state.version} · {state.status}
              </span>
            </div>
          )}
        </div>

        {/* Product switcher pills — wraps to multiple lines on narrow viewports. */}
        <div
          role="tablist"
          aria-label="Editorial charter products"
          className="inline-flex flex-wrap items-center gap-1 rounded-lg p-1 bg-hm-bg-1 border border-hm-border mb-6 max-w-full"
          data-testid="governance-product-switcher"
        >
          {GOV_PRODUCTS.map((p) => {
            const active = p.slug === product;
            return (
              <button
                key={p.slug}
                role="tab"
                aria-selected={active}
                onClick={() => requestSwitch(p.slug)}
                data-testid={`governance-tab-${p.slug}`}
                className={
                  "h-8 px-4 rounded-md text-xs font-medium transition-colors " +
                  (active
                    ? "bg-hm-accent text-hm-bg"
                    : "text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-2")
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {state.loading ? (
          <div className="rounded-lg border border-hm-border" style={{
            minHeight: 620, background: "#0F1013",
          }}>
            <div className="p-6 space-y-2">
              <div className="h-3 bg-hm-bg-2 rounded w-3/4 animate-pulse"></div>
              <div className="h-3 bg-hm-bg-2 rounded w-2/3 animate-pulse"></div>
              <div className="h-3 bg-hm-bg-2 rounded w-4/5 animate-pulse"></div>
            </div>
          </div>
        ) : state.error ? (
          <div className="p-4 rounded-lg border border-hm-danger/40 bg-hm-danger/5" data-testid="governance-error">
            <div className="text-hm-danger text-sm font-medium">Failed to load charter</div>
            <p className="text-xs text-hm-fg-muted mt-1 mono">{state.error}</p>
          </div>
        ) : state.not_found ? (
          <div className="p-6 rounded-lg border border-hm-border bg-hm-bg-1 text-center" data-testid="governance-not-found">
            <div className="text-hm-fg-muted text-sm">
              No active charter for {activeProduct.label} yet.
            </div>
            <p className="text-xs text-hm-fg-subtle mt-2">
              Contact the platform admin to seed one.
            </p>
          </div>
        ) : (
          <textarea
            value={editing ? draft : state.raw_markdown || ""}
            readOnly={!editing}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            data-testid="governance-textarea"
            className={
              "w-full block resize-none rounded-lg px-5 py-4 " +
              "font-mono text-[13px] leading-[1.65] " +
              (editing
                ? "text-hm-fg border border-hm-accent-dim outline-none focus:ring-2 focus:ring-hm-accent-dim/40 cursor-text"
                : "text-hm-fg-muted border border-hm-border cursor-not-allowed")
            }
            style={{
              background: "#0F1013",
              minHeight: "620px",
              maxHeight: "70vh",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          />
        )}

        {!state.loading && !state.error && !state.not_found && (
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <div className="text-[11px] text-hm-fg-subtle mono">
              {state.last_edited_at ? (
                <>
                  last edit ·{" "}
                  <span className="text-hm-fg-muted">
                    {new Date(state.last_edited_at).toLocaleString(undefined, {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                  {(state.editor_name || state.editor_email) && (
                    <>
                      {" · "}
                      <span className="text-hm-fg-muted">
                        {state.editor_name || state.editor_email}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <>seeded from source — no manual edit yet</>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2">
              {editing ? (
                <>
                  <button
                    onClick={cancelEdit}
                    disabled={saving}
                    data-testid="governance-cancel"
                    className="h-9 px-4 rounded-md text-xs border border-hm-danger/40 text-hm-danger hover:bg-hm-danger/10 disabled:opacity-40"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid="governance-save"
                    className="h-9 px-4 rounded-md text-xs bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim inline-flex items-center gap-1.5 disabled:opacity-40"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    Save
                  </button>
                </>
              ) : (
                <button
                  onClick={startEdit}
                  data-testid="governance-edit"
                  className="h-9 px-4 rounded-md text-xs bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim"
                >
                  Modify
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirm-switch modal for unsaved edits */}
      {pendingSwitch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="governance-switch-modal">
          <div className="hm-surface w-full max-w-md p-6 mx-4 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-hm-warn mt-0.5" />
              <div className="flex-1">
                <h3 className="text-hm-fg font-semibold">
                  Unsaved changes will be lost
                </h3>
                <p className="text-xs text-hm-fg-muted mt-2 leading-relaxed">
                  Switching product will discard the edits you made to{" "}
                  <span className="mono">{activeProduct.label}</span>.
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={cancelSwitch}
                data-testid="governance-switch-cancel"
                className="h-9 px-4 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong"
              >
                Keep editing
              </button>
              <button
                onClick={confirmSwitch}
                data-testid="governance-switch-confirm"
                className="h-9 px-4 rounded-md text-xs bg-hm-danger/10 border border-hm-danger/40 text-hm-danger hover:bg-hm-danger/20"
              >
                Discard changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Turn CC · 7 — Sources blocklist tab.
// ---------------------------------------------------------------------------
function SourcesBlocklistTab() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [reasonInput, setReasonInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // row being confirmed

  const load = async () => {
    try {
      const r = await fetch(`${API}/governance/blocked-sources`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setRows(body.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    const d = domainInput.trim();
    if (!d) {
      toast.error("Enter a domain.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/governance/blocked-sources`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ domain: d, reason: reasonInput.trim() || null }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      toast.success(`Blocked: ${body.registered_root} · matches every TLD`);
      setDomainInput("");
      setReasonInput("");
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(`Block failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const doRemove = async (row) => {
    try {
      const r = await fetch(`${API}/governance/blocked-sources/${row.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${r.status}`);
      }
      toast.success(`Unblocked: ${row.registered_root}`);
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(`Remove failed: ${e.message}`);
    }
  };

  if (error) {
    return (
      <div className="hm-surface p-6" data-testid="sources-error">
        <div className="text-hm-danger font-medium">Failed to load blocklist</div>
        <p className="text-xs text-hm-fg-muted mt-2 mono">{error}</p>
      </div>
    );
  }
  if (rows === null) return <SettingsTabSkeleton />;

  return (
    <div className="mx-auto w-full max-w-4xl" data-testid="sources-panel">
      <div className="hm-surface p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-hm-fg text-lg font-semibold tracking-tight flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-hm-accent" />
              Sources blocklist
            </h2>
            <p className="text-xs text-hm-fg-muted mt-1 max-w-xl leading-relaxed">
              Domains excluded from every Product 1 collection pipeline. Enforced
              at ingestion, before LLM scoring — blocked sources cost nothing.
              Matching is done on the registered domain root, so
              <code className="mono text-[11px] mx-1">gymfactory</code>
              silently blocks every TLD variant.
            </p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="sources-add-btn"
            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-hm-accent text-hm-bg text-xs font-medium hover:bg-hm-accent-dim"
          >
            <Plus className="h-3.5 w-3.5" />
            Block a domain
          </button>
        </div>

        <div className="mt-6 rounded-lg border border-hm-border overflow-hidden">
          <div className="grid grid-cols-[1.2fr_2fr_0.9fr_auto] gap-4 px-5 py-3 bg-hm-bg-1 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
            <div>Domain</div>
            <div>Reason</div>
            <div>Blocked</div>
            <div></div>
          </div>
          {rows.length === 0 ? (
            <div className="px-5 py-10 text-center mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
              No blocked sources yet.
            </div>
          ) : (
            <div className="divide-y divide-hm-border">
              {rows.map((r) => (
                <div
                  key={r.id}
                  data-testid={`sources-row-${r.registered_root}`}
                  className="grid grid-cols-[1.2fr_2fr_0.9fr_auto] gap-4 px-5 py-4 items-start hover:bg-hm-bg-1/40 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="mono text-sm text-hm-fg font-medium">
                      {r.registered_root}
                    </div>
                    <div className="mono text-[10px] text-hm-fg-subtle mt-1">
                      matches *.{r.registered_root}
                    </div>
                  </div>
                  <div className="text-xs text-hm-fg-muted leading-relaxed break-words">
                    {r.reason || (
                      <span className="mono text-hm-fg-subtle">—</span>
                    )}
                  </div>
                  <div className="mono text-[11px] text-hm-fg-muted">
                    {r.blocked_at
                      ? new Date(r.blocked_at).toLocaleDateString(undefined, {
                          day: "2-digit", month: "short", year: "numeric",
                        })
                      : "—"}
                    {r.blocked_by_email && (
                      <div className="mono text-[10px] text-hm-fg-subtle mt-0.5">
                        by {r.blocked_by_email}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setPendingDelete(r)}
                    data-testid={`sources-remove-${r.registered_root}`}
                    className="h-8 px-3 rounded-md text-xs border border-hm-border text-hm-fg-subtle hover:text-hm-danger hover:border-hm-danger/40 inline-flex items-center gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="sources-add-modal">
          <div className="hm-surface w-full max-w-lg p-6 mx-4 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-hm-fg font-semibold flex items-center gap-2">
                <Ban className="h-4 w-4 text-hm-accent" />
                Block a domain
              </h3>
              <button
                onClick={() => setShowAdd(false)}
                className="p-1 rounded-md text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle block mb-1">
                  Domain
                </label>
                <input
                  autoFocus
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  placeholder="example.com"
                  data-testid="sources-domain-input"
                  className="w-full h-9 px-3 rounded-md bg-hm-bg-1 border border-hm-border mono text-sm text-hm-fg focus:outline-none focus:border-hm-accent-dim"
                />
                <p className="mono text-[10px] text-hm-fg-subtle mt-1">
                  All TLD variants ({"*.<root>"}) will be matched automatically.
                </p>
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle block mb-1">
                  Reason (optional)
                </label>
                <textarea
                  rows={3}
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  placeholder="Why is this domain off-brand for Product 1?"
                  data-testid="sources-reason-input"
                  className="w-full px-3 py-2 rounded-md bg-hm-bg-1 border border-hm-border text-sm text-hm-fg focus:outline-none focus:border-hm-accent-dim resize-none"
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowAdd(false)}
                disabled={busy}
                data-testid="sources-cancel"
                className="h-9 px-4 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy || !domainInput.trim()}
                data-testid="sources-submit"
                className="h-9 px-4 rounded-md text-xs bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
                Block
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove confirm modal */}
      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="sources-confirm-modal">
          <div className="hm-surface w-full max-w-md p-6 mx-4 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-hm-warn mt-0.5" />
              <div className="flex-1">
                <h3 className="text-hm-fg font-semibold">
                  Unblock <span className="mono">{pendingDelete.registered_root}</span>?
                </h3>
                <p className="text-xs text-hm-fg-muted mt-2 leading-relaxed">
                  Content from this domain will start appearing in future
                  Product 1 briefs again.
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setPendingDelete(null)}
                data-testid="sources-confirm-cancel"
                className="h-9 px-4 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong"
              >
                Keep blocked
              </button>
              <button
                onClick={() => doRemove(pendingDelete)}
                data-testid="sources-confirm-remove"
                className="h-9 px-4 rounded-md text-xs bg-hm-danger/10 border border-hm-danger/40 text-hm-danger hover:bg-hm-danger/20 inline-flex items-center gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Unblock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AdvancedTab() {
  /*
   * Turn CC · 13 — v1 force-regenerate button removed. Daily briefs are
   * now generated exclusively by the pipeline_v2 cron at 05:00 SGT.
   * Manual regeneration must go through `POST /api/daily-brief-v2/full-run`.
   */
  return (
    <div className="hm-surface p-6 max-w-2xl" data-testid="settings-advanced">
      <div className="flex items-start gap-3 pb-4 border-b border-hm-border">
        <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: "#B45309", marginTop: 2 }} />
        <div>
          <div className="text-hm-fg font-medium">Automation</div>
          <div className="mono text-[11px] text-hm-fg-muted mt-1">
            Daily briefs run automatically at 05:00 Asia/Singapore via
            pipeline_v2. Reasoning refresh runs at 07:30 &amp; 19:30
            Brisbane. Weekly briefs run Monday 04:00 SGT via pipeline_v2_weekly.
            Nothing to configure here.
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-2">
          Manual regeneration (advanced)
        </div>
        <div className="mono text-[11px] text-hm-fg-muted">
          Use <code>POST /api/daily-brief-v2/full-run</code> for daily and
          <code>POST /api/weekly-brief-v2/generate</code> for weekly. These
          endpoints are idempotent (they skip if a brief for the target
          date already exists).
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [user, setUser] = useState(null);
  const initialTab = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const t = p.get("tab");
      return TABS.some((x) => x.key === t) ? t : "profile";
    } catch {
      return "profile";
    }
  })();
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { headers: authHeaders() });
        if (res.ok) setUser(await res.json());
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", headers: authHeaders() }).catch(
      () => {},
    );
    auth.clear();
    window.location.assign("/login");
  };

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Settings"
        badge="Live"
        subtitle="Profile, feature flags, integrations, and the danger zone."
      />
      <div className="flex flex-col md:flex-row gap-6">
        <nav className="md:w-52 shrink-0 flex md:flex-col gap-1">
          {TABS.map((t) => (
            <TabButton
              key={t.key}
              testId={`settings-tab-${t.key}`}
              active={tab === t.key}
              onClick={() => setTab(t.key)}
              onHover={() => prefetchSettingsTab[t.key]?.()}
              icon={t.icon}
              label={t.label}
            />
          ))}
        </nav>
        <div className="flex-1 min-w-0">
          {tab === "profile" && <ProfileTab user={user} />}
          {tab === "flags" && <FlagsTab />}
          {tab === "keys" && <KeysTab />}
          {tab === "integrations" && <IntegrationsTab />}
          {tab === "governance" && <GovernanceTab />}
          {tab === "sources" && <SourcesBlocklistTab />}
          {tab === "advanced" && <AdvancedTab />}
          {tab === "danger" && <DangerTab onLogout={logout} />}
        </div>
      </div>
    </div>
  );
}
